import React, { useEffect, useRef } from "react";
import styled, { css, keyframes } from "styled-components";
import { tokens } from "../../public/tokens";
import Button from "./Button";
import PlainIconButton from "./PlainIconButton";

const overlayFadeIn = keyframes({
    from: { opacity: 0 },
    to: { opacity: 1 },
});

const modalEnter = keyframes({
    from: { opacity: 0, transform: "translateY(12px) scale(0.96)" },
    to: { opacity: 1, transform: "translateY(0) scale(1)" },
});

const StyledOverlay = styled.div({
    position: "fixed",
    inset: 0,
    padding: tokens.globals["spacing-md"],
    background: "rgba(0, 0, 0, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    animation: css`${overlayFadeIn} 260ms ease-out`,
});

/*
 * A keyframes() result wrapped in css`` only flattens correctly when the
 * surrounding object is a plain top-level object, not one returned from a
 * props function — so `width` (the only prop-dependent value here) is a
 * per-property function instead of wrapping the whole object.
 */
const StyledModal = styled.div({
    /* A number is treated as px; a string (e.g. "90%") is used as-is. */
    width: ({ $width = 416 }) => (typeof $width === "number" ? `${$width}px` : $width),
    maxWidth: "100%",
    maxHeight: `calc(100vh - ${tokens.globals["spacing-8"]})`,
    overflow: "hidden",
    padding: ({ $headerTone }) => ($headerTone ? 0 : tokens.globals["spacing-2xl"]),
    position: "relative",
    background: tokens.colors.surfaces["container-background"],
    boxShadow: tokens.effects.effects.shadow.hover,
    borderRadius: tokens.globals["border-radius"],
    border: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: ({ $headerTone }) => ($headerTone ? 0 : tokens.globals["spacing-md"]),
    animation: css`${modalEnter} 380ms cubic-bezier(0.22, 1, 0.36, 1)`,

    "&:focus": {
        outline: "none",
    },
});

const StyledClose = styled(PlainIconButton)({
    position: "absolute",
    top: tokens.globals["spacing-md"],
    right: tokens.globals["spacing-md"],
});

const StyledTitle = styled.h2({
    margin: 0,
    padding: `0 ${tokens.globals["spacing-9"]}`,
    textAlign: "center",
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["3xl-24px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.bold,
    lineHeight: tokens.typography.lineHeights["110"],
});

/**
 * Full-width colored band used as the modal header when `headerTone` is set.
 */
const StyledHeaderBand = styled.div({
    alignSelf: "stretch",
    width: "100%",
    padding: tokens.globals["spacing-md"],
    position: "relative",
    display: "flex",
    alignItems: "center",
    background: tokens.colors.theme["topbar-color"],
    borderRadius: `${tokens.globals["border-radius"]} ${tokens.globals["border-radius"]} 0 0`,
});

const StyledHeaderTitle = styled.h2({
    margin: 0,
    paddingRight: tokens.globals["spacing-8"],
    color: tokens.colors.neutral.grey["100"],
    fontSize: tokens.typography.fontSize["base-16px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: tokens.typography.lineHeights["110"],
});

const StyledHeaderClose = styled(PlainIconButton)({
    position: "absolute",
    top: "50%",
    right: tokens.globals["spacing-md"],
    transform: "translateY(-50%)",
});

const StyledDescription = styled.p({
    margin: 0,
    maxWidth: "390px",
    textAlign: "center",
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["base-16px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    lineHeight: tokens.typography.lineHeights["110"],
});

const StyledBody = styled.div({
    width: "100%",
    minHeight: 0,
    flex: "1 1 auto",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.globals["spacing-md"],
});

const StyledActions = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.globals["spacing-md"],
    flexWrap: "wrap",
});

/**
 * @typedef {Object} ModalAction
 * @property {React.ReactNode} label
 * @property {(event: React.MouseEvent) => void} [onClick]
 * @property {"primary"|"secondary"|"destructive"} [tone] Defaults per button slot; not inferred.
 * @property {React.ReactNode} [loadingLabel] Shown instead of `label` while loading.
 * Also: icon, disabled, loading, title, disabledReason (self-explanatory).
 */

/**
 * Standard AIE modal dialog: overlay, title/description, content, and up to two action buttons.
 * @param {React.ReactNode} props.title Dialog title, also used as the `aria-label`.
 * @param {ModalAction} [props.primaryAction] Primary button; omit to hide, defaults tone "primary".
 * @param {ModalAction} [props.secondaryAction] Secondary button; omit to hide, defaults tone "secondary".
 * @param {(event?: React.SyntheticEvent) => void} [props.onClose] Close handler; also shows the close (x) button.
 * @param {number|string} [props.width] Number is px; string (e.g. "90%") is used as-is. Defaults to 416.
 * @param {"brand"} [props.headerTone] Renders the title in a full-width colored header band instead of plain text.
 * Also: description, children, closeOnOverlay, closeOnEscape, className (self-explanatory).
 */
function Modal({
    title,
    description,
    children,
    primaryAction,
    secondaryAction,
    onClose,
    width = 416,
    closeOnOverlay = true,
    closeOnEscape = true,
    headerTone,
    className,
}) {
    const modalRef = useRef(null);

    useEffect(() => {
        if (modalRef.current) {
            modalRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (!closeOnEscape || !onClose) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose(event);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [closeOnEscape, onClose]);

    const handleOverlayClick = (event) => {
        if (closeOnOverlay && onClose && event.target === event.currentTarget) {
            onClose(event);
        }
    };

    const renderAction = (action, defaultTone) => {
        if (!action) {
            return null;
        }

        return (
            <Button
                type="button"
                variant={action.tone || defaultTone}
                onClick={action.onClick}
                disabled={Boolean(action.disabled || action.loading)}
                title={action.disabled ? action.disabledReason : action.title}
            >
                {action.icon && (
                    <i className={`fal fa-${action.icon}`} aria-hidden="true" />
                )}
                {action.loading ? action.loadingLabel || action.label : action.label}
            </Button>
        );
    };

    return (
        <StyledOverlay onMouseDown={handleOverlayClick}>
            <StyledModal
                ref={modalRef}
                $width={width}
                $headerTone={headerTone}
                className={className}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                tabIndex={-1}
            >
                {headerTone ? (
                    <StyledHeaderBand>
                        <StyledHeaderTitle>{title}</StyledHeaderTitle>
                        {onClose && (
                            <StyledHeaderClose variant="inverse" size="md" onClick={onClose} aria-label="Close dialog">
                                <i className="fal fa-times" aria-hidden="true" />
                            </StyledHeaderClose>
                        )}
                    </StyledHeaderBand>
                ) : (
                    <>
                        {onClose && (
                            <StyledClose size="md" onClick={onClose} aria-label="Close dialog">
                                <i className="fal fa-times" aria-hidden="true" />
                            </StyledClose>
                        )}
                        <StyledTitle>{title}</StyledTitle>
                    </>
                )}
                <StyledBody>
                    {description && <StyledDescription>{description}</StyledDescription>}
                    {children}
                </StyledBody>
                {(primaryAction || secondaryAction) && (
                    <StyledActions>
                        {renderAction(primaryAction, "primary")}
                        {renderAction(secondaryAction, "secondary")}
                    </StyledActions>
                )}
            </StyledModal>
        </StyledOverlay>
    );
}

Modal.displayName = "Modal";

export default Modal;
